import React, { useEffect, useMemo, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const BAND_TONE = { High: undefined, Medium: "warn", Low: "danger", Unknown: "muted" };
const DISP_TONE = {
  NOT_SENSITIVE: undefined,
  UNDISPOSED: "danger",
  LEGITIMATE_CONTEXT: undefined,
  PROTECTED_FROM_PERSUASION: "warn",
  PROHIBITED_TARGETING: "danger",
};
const DISP_LABEL = {
  NOT_SENSITIVE: "Not sensitive",
  UNDISPOSED: "Needs disposition",
  LEGITIMATE_CONTEXT: "Legitimate context",
  PROTECTED_FROM_PERSUASION: "Protected from persuasion",
  PROHIBITED_TARGETING: "Prohibited targeting",
};
const DISPOSITION_OPTIONS = [
  { value: "LEGITIMATE_CONTEXT", label: "Legitimate context — may be discussed as context, not persuasively" },
  { value: "PROTECTED_FROM_PERSUASION", label: "Protected from persuasion — never use for targeting or persuasion" },
  { value: "PROHIBITED_TARGETING", label: "Prohibited targeting — never surface at all" },
];

function renderValue(value) {
  if (value === null || value === undefined || value === "") return <em style={{ color: "var(--bo-muted)" }}>—</em>;
  if (Array.isArray(value)) {
    return <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{value.map((v, i) => <Pill key={`${v}-${i}`}>{String(v)}</Pill>)}</div>;
  }
  return <span>{String(value)}</span>;
}

function SensitivityForm({ statement, onSaved }) {
  const [open, setOpen] = useState(false);
  const [disposition, setDisposition] = useState(statement.sensitivity_disposition || "");
  const [rationale, setRationale] = useState(statement.sensitivity_rationale || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setSaving(true);
    try {
      await api.post(`/intelligence/statements/${statement.id}/sensitivity`, {
        disposition,
        rationale: rationale.trim() || null,
      });
      onSaved();
      setOpen(false);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="bo-btn bo-btn-ghost"
        style={{ padding: "4px 10px", fontSize: 12, marginTop: 6 }}
        onClick={() => setOpen(v => !v)}
        data-testid={`ab-set-disp-btn-${statement.id}`}
      >
        {open ? "Cancel" : (statement.sensitivity_disposition ? "Change disposition" : "Set disposition")}
      </button>
      {open && (
        <form
          onSubmit={submit}
          style={{ marginTop: 8, padding: 10, background: "var(--bo-panel-alt)",
                   border: "1px solid var(--bo-line)", borderRadius: "var(--bo-radius-sm)" }}
        >
          <div className="bo-eyebrow">Disposition</div>
          {DISPOSITION_OPTIONS.map(opt => (
            <label key={opt.value} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 6, cursor: "pointer" }}>
              <input type="radio" name={`disp-${statement.id}`} value={opt.value}
                checked={disposition === opt.value}
                onChange={() => setDisposition(opt.value)}
                data-testid={`ab-disp-${statement.id}-${opt.value}`} />
              <span style={{ fontSize: 13 }}>{opt.label}</span>
            </label>
          ))}
          <input
            className="bo-input"
            value={rationale}
            onChange={e => setRationale(e.target.value)}
            placeholder="Rationale (recorded in audit trail)"
            style={{ marginTop: 10 }}
            required
            data-testid={`ab-rationale-${statement.id}`}
          />
          {err && <div className="bo-login-error" role="alert" style={{ marginTop: 8 }}>{err}</div>}
          <button type="submit" className="bo-btn bo-btn-primary"
            disabled={saving || !disposition} style={{ marginTop: 10 }}
            data-testid={`ab-disp-submit-${statement.id}`}>
            {saving ? "Saving…" : "Save disposition"}
          </button>
        </form>
      )}
    </>
  );
}

function StatementCard({ statement, onRefresh }) {
  const sens = statement.sensitivity || { is_sensitive: false, disposition: "NOT_SENSITIVE" };
  return (
    <div className="bo-card" style={{ padding: 16, marginBottom: 10 }}
         data-testid={`ab-field-${statement.field_path}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "var(--bo-mono)", fontSize: 12, color: "var(--bo-muted)" }}>{statement.field_path}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {sens.is_sensitive && (
            <Pill tone={DISP_TONE[sens.disposition]} testId={`ab-disp-pill-${statement.field_path}`}>
              Sensitive · {DISP_LABEL[sens.disposition] || sens.disposition}
            </Pill>
          )}
          <Pill tone={BAND_TONE[statement.confidence_band]}>{statement.confidence_band || "—"}</Pill>
          <Pill tone="muted">{(statement.confidence_factors || {}).authority || "—"}</Pill>
        </div>
      </div>
      <div style={{ marginTop: 10 }}>{renderValue(statement.value)}</div>
      {sens.is_sensitive && statement.sensitivity_rationale && (
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--bo-muted)", fontStyle: "italic" }}>
          Rationale: {statement.sensitivity_rationale}
        </div>
      )}
      {sens.is_sensitive && <SensitivityForm statement={statement} onSaved={onRefresh} />}
    </div>
  );
}

export default function AudienceBrain() {
  const [audiences, setAudiences] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (audienceId) => {
    if (!audienceId) return;
    try {
      const r = await api.get(`/intelligence/brains/audience/${audienceId}`);
      setData(r.data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/audiences");
        const list = r.data.audiences || [];
        setAudiences(list);
        if (list.length > 0) {
          setSelectedId(list[0].id);
          await load(list[0].id);
        }
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    if (!data?.statements) return {};
    const out = {};
    for (const s of data.statements) {
      const d = (s.field_path || "").split(".")[0] || "other";
      if (!out[d]) out[d] = [];
      out[d].push(s);
    }
    return out;
  }, [data]);

  const counts = data?.sensitivity_counts || {};
  const undisposedCount = counts.UNDISPOSED || 0;

  return (
    <Shell title="Audience Brain">
      <div className="bo-eyebrow" data-testid="ab-eyebrow">Gate 2 · Slice 4 · Audience Brain</div>
      <h1 className="bo-serif" style={{ fontSize: 34, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "6px 0 8px" }} data-testid="ab-title">
        Audience Brain
      </h1>
      <p style={{ color: "var(--bo-muted)", maxWidth: 720, margin: "0 0 20px 0" }} data-testid="ab-lede">
        Every audience has a Brain. Sensitive attributes (health, minors, protected classes) must
        carry an explicit disposition before generation can use them. Undisposed fields are blocked
        by default — this is OWNER-01 enforced deterministically, not by the model.
      </p>

      {loading && <LoadingCard label="Loading audiences…" />}
      <ErrorBanner error={error} />

      {!loading && audiences.length === 0 && (
        <EmptyState
          title="No audiences yet"
          description="Create an audience under Products first — Audience Brains will appear here once statements land."
          testId="ab-empty-no-audiences"
        />
      )}

      {!loading && audiences.length > 0 && (
        <div className="bo-card" style={{ marginBottom: 16 }}>
          <label style={{ display: "block" }}>
            <div className="bo-eyebrow">Audience</div>
            <select className="bo-input" style={{ marginTop: 6 }} value={selectedId}
              onChange={e => { setSelectedId(e.target.value); load(e.target.value); }}
              data-testid="ab-audience-select">
              {audiences.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
          {data && (
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }} data-testid="ab-counts">
              <Pill tone={undefined}>Total: {data.statement_count || 0}</Pill>
              <Pill tone={undisposedCount > 0 ? "danger" : "muted"} testId="ab-count-undisposed">
                Needs disposition: {undisposedCount}
              </Pill>
              <Pill tone="muted">Legitimate: {counts.LEGITIMATE_CONTEXT || 0}</Pill>
              <Pill tone="warn">Protected: {counts.PROTECTED_FROM_PERSUASION || 0}</Pill>
              <Pill tone="danger">Prohibited: {counts.PROHIBITED_TARGETING || 0}</Pill>
            </div>
          )}
        </div>
      )}

      {data && undisposedCount > 0 && (
        <div className="bo-card" style={{ marginBottom: 16, borderColor: "var(--bo-danger, #b34a4a)" }} data-testid="ab-guardrail-banner">
          <div className="bo-eyebrow">Guardrail · OWNER-01</div>
          <h2 className="bo-card-title" style={{ margin: "4px 0" }}>
            {undisposedCount} sensitive field{undisposedCount === 1 ? "" : "s"} awaiting disposition
          </h2>
          <p className="bo-card-lede">
            Copywriter and Guardian will block these fields from generation until a reviewer sets a
            disposition. Scroll down to the flagged rows and set each one.
          </p>
        </div>
      )}

      {data && Object.entries(grouped).map(([domain, statements]) => (
        <section key={domain} className="bo-card" style={{ marginBottom: 16 }} data-testid={`ab-domain-${domain}`}>
          <div className="bo-eyebrow">Domain</div>
          <h2 className="bo-card-title" style={{ textTransform: "capitalize" }}>{domain.replace(/_/g, " ")}</h2>
          <div style={{ marginTop: 12 }}>
            {statements.map(s => (
              <StatementCard key={s.id || s.field_path} statement={s} onRefresh={() => load(selectedId)} />
            ))}
          </div>
        </section>
      ))}

      {data && (data.statement_count || 0) === 0 && (
        <EmptyState
          title="No audience statements yet"
          description="Add statements via the extraction pipeline or hand-entry to see them appear here."
          testId="ab-empty-no-statements"
        />
      )}
    </Shell>
  );
}
